<template>
  <div class="recent-view">
    <div class="container-fluid">
      <section>
        <!-- Game container - GameManager will inject iframe here -->
        <div 
          v-if="canPlay" 
          ref="gameContainer" 
          class="game-container"
          :class="{ 'game-throttled': isGameThrottled }"
        >
          <!-- Game iframe will be dynamically inserted here by GameManager -->
        </div>

        <div v-else class="w-100 d-flex align-items-center justify-content-center" 
          :style="{
            height: '70vh',
            backgroundColor: '#262653',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundImage: `url(${game.thumb_1})`
          }">
          <h1 class="text-white text-center p-3 subscription-message" @click="openSubscription">
            <span v-if="isLoggedIn">
              {{ $t("Your subscription finished, top-up balance and") }}
              {{ $t("activate subscription") }}.
            </span>
            <span v-else>
              {{ $t("Please login to play this game") }}, {{ $t("click") }}
              {{ $t("here") }} {{ $t("to login") }}.
            </span>
          </h1>
        </div>

        <div class="d-flex flex-column flex-md-row justify-content-between gap-4 my-4">
          <!-- Title -->
          <div class="d-flex flex-column mx-2">
            <div class="d-flex justify-content-between">
              <div>
                <h4>{{ game.title }}</h4>
                <p class="small">
                  {{ $t("Played") }} {{ game.views ? game.views : 0 }}
                  {{ $t("times") }}.
                </p>
              </div>
              <div class="d-flex align-items-center justify-content-center rounded-circle bg-dark py-2 px-4 d-md-none"
                @click="safeToggleFullscreen">
                <img src="@/assets/img/game/fullscreen.svg" alt="fullscreen" />
              </div>
            </div>
            <!-- Reviews -->
            <div class="d-flex align-items-center">
              <StarRating :rating="game.ratings_avg" starSize="18" :readonly="true" />
              <span class="small">
                {{ game.ratings_avg }} ({{ game.ratings_count }}
                {{ $t("Reviews") }})
              </span>
            </div>
            <!-- Share -->
            <div class="d-flex flex-wrap">
              <img src="@/assets/img/share/icons8-facebook-500.png" alt="facebook" class="cursor-pointer me-2"
                style="width: 40px; height: 40px" @click="safeShare('facebook', game)" />
              <img src="@/assets/img/share/icons8-twitterx-500.png" alt="twitter" class="cursor-pointer me-2"
                style="width: 40px; height: 40px" @click="safeShare('x', game)" />
              <img src="@/assets/img/share/icons8-instagram.svg" alt="instagram" class="cursor-pointer me-2"
                style="width: 48px; height: 40px" @click="safeShare('instagram', game)" />
              <img src="@/assets/img/share/icons8-linkedin-500.png" alt="linkedin" class="cursor-pointer me-2"
                style="width: 40px; height: 40px" @click="safeShare('linkedin', game)" />
              <img src="@/assets/img/share/icons8-whatsapp-500.png" alt="whatsapp" class="cursor-pointer"
                style="width: 40px; height: 40px" @click="safeShare('whatsapp', game)" />
            </div>
          </div>
          <div class="p-4 text-center" v-if="isLoggedIn">
            <StarRating @input="safeChangeRating" starSize="42" :rating="userRate" />
            <p class="">{{ $t("Rate this game") }}</p>
          </div>

          <!-- Fullscreen Options -->
          <div class="d-none d-md-block mx-2">
            <div class="d-flex align-items-center rounded-circle bg-dark py-2 px-4 cursor-pointer"
              @click="safeToggleFullscreen">
              <img src="@/assets/img/game/fullscreen.svg" alt="fullscreen" />
              <span class="text-white ms-2">{{ $t("Fullscreen") }}</span>
            </div>
          </div>
        </div>
      </section>

      <hr class="bg-secondary my-3" />

      <section>
        <div class="mx-2 my-3">
          <h4 class="my-2">{{ $t("Description") }}:</h4>
          <p class="text-md text-justify text-gray" v-html="game.description"></p>
        </div>
        <div class="mx-2 my-3">
          <h4 class="my-2">{{ $t("Instructions") }}:</h4>
          <p class="text-md text-justify text-gray" v-html="game.instructions"></p>
        </div>
      </section>

      <hr class="bg-secondary my-3" />
    </div>
    <div class="crime-section pb-60" v-if="similarGames.length > 0">
      <div class="container-fluid">
        <div class="section-wrapper">
          <h4 class="left-title">{{ $t("Similar Games") }}</h4>
        </div>
        <MostViews :items="similarGames" />
      </div>
    </div>
    <div class="crime-section pb-60">
      <div class="container-fluid">
        <div class="section-wrapper">
          <h4 class="left-title">{{ $t("New Games") }}</h4>
        </div>
        <NewGames :items="newGames" />
      </div>
    </div>
    <hr class="bg-secondary my-3" />
  </div>
</template>

<script>
import { useGamesStore } from "@/store/games";
import { mapState, mapActions } from "pinia";
import { useAuthStore } from "@/store/auth";
import helpers from "@/helpers";
import StarRating from "@/components/partials/StarRating.vue";
import MostViews from "@/components/partials/GameRowSlider.vue";
import NewGames from "@/components/partials/GameRoundedSlider.vue";
import { GameManager } from '@/game-manager'; // Import the GameManager

export default {
  name: "GamePage",
  components: {
    StarRating,
    MostViews,
    NewGames,
  },
  data() {
    return {
      play: false,
      close: true,
      canPlay: false,
      comment: "",
      gameManager: null, // Initialize gameManager
      isGameThrottled: false,
      isPageVisible: true
    };
  },
  computed: {
    ...mapState(useAuthStore, ["isLoggedIn", "userInfo"]),
    ...mapState(useGamesStore, [
      "game",
      "userRate",
      "newGames",
      "similarGames",
      "voteAction",
      "comments",
    ]),
  },
  methods: {
    ...mapActions(useGamesStore, [
      "fetchGame",
      "voteGame",
      "postComment",
      "rateGame",
    ]),
    ...mapActions(useAuthStore, [
      "openPaymentPage",
    ]),
    
    // Safe wrapper methods
    async safeChangeRating(value) {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          this.changeRating(value);
        });
      } else {
        setTimeout(() => this.changeRating(value), 0);
      }
    },
    
    safeToggleFullscreen() {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          this.toggleFullscreen();
        });
      } else {
        setTimeout(() => this.toggleFullscreen(), 0);
      }
    },
    
    safeShare(platform, game) {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          this.share(platform, game);
        });
      } else {
        setTimeout(() => this.share(platform, game), 0);
      }
    },
    
    // Original methods
    async changeRating(value) {
      await this.rateGame(this.game.id, value);
      helpers.showToast(
        this.$t("Your rating has been saved successfully"),
        this.$t("Success"),
        "success"
      );
    },
    
    openSubscription() {
      localStorage.setItem("redirectUrl", this.$route.fullPath);
      let config = helpers.getConfig();
      if(config.subscription_type == "card"){
        if(!this.isLoggedIn){
          this.openLogin();
        } else {
          this.openPaymentPage()
        }
      } else {
        window.location.href = config.landing_page_url;
      }
    },
    
    toggleFullscreen() {
      if (this.gameManager) {
        this.gameManager.toggleFullscreen();
      }
    },
    
    onEscKey(event) {
      if (event.key === "Escape") {
        if (document.fullscreenElement) {
          document.exitFullscreen();
        }
        
        if (this.gameManager) {
          this.gameManager.exitIOSFullscreen();
        }
      }
    },
    
    // Handle page visibility changes
    handleVisibilityChange() {
      this.isPageVisible = !document.hidden;
      
      if (this.gameManager) {
        if (this.isPageVisible) {
          this.gameManager.resumeGame();
          this.isGameThrottled = false;
        } else {
          this.gameManager.pauseGame();
          this.isGameThrottled = true;
        }
      }
    },
    
    // Load game using GameManager
    async loadGameSafely() {
      if (!this.game?.url || !this.canPlay) return;
      
      try {
        // Initialize GameManager
        this.gameManager = new GameManager();
        
        // Load game into the container
        await this.gameManager.loadGame(this.game.url, this.$refs.gameContainer);
        
        console.log('Game loaded successfully with GameManager');
        
        // Throttle game if page is not visible
        if (!this.isPageVisible) {
          this.gameManager.pauseGame();
          this.isGameThrottled = true;
        }
      } catch (error) {
        console.error('Failed to load game with GameManager:', error);
        this.canPlay = false;
        
        // Show error message to user
        helpers.showToast(
          this.$t("Failed to load the game. Please try again."),
          this.$t("Error"),
          "error"
        );
      }
    },

    // Keep your existing share method
    share(platform, game) {
      if (!game || !game.id) {
        console.error("Game data is undefined or missing required properties.");
        return;
      }

      const url = `${window.location.origin}/game/${this.$route.params.slug}`;
      const title = encodeURIComponent(game.title);

      switch (platform) {
        case "facebook":
          window.open(
            `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
            "_blank"
          );
          break;
        case "x":
          window.open(
            `https://twitter.com/intent/tweet?text=${title}&url=${encodeURIComponent(url)}`,
            "_blank"
          );
          break;
        case "instagram":
          helpers.showToast(
            this.$t("To share on Instagram, copy the game link and share it on your story or post."),
            this.$t("Success"),
            "success"
          );
          navigator.clipboard.writeText(url);
          break;
        case "whatsapp":
          window.open(
            `https://api.whatsapp.com/send?text=${title} - ${encodeURIComponent(url)}`,
            "_blank"
          );
          break;
        case "linkedin":
          window.open(
            `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
            "_blank"
          );
          break;
        default:
          console.error("Unsupported platform:", platform);
      }
    }
  },
  
  mounted() {
    document.addEventListener("fullscreenchange", this.onFullscreenChange);
    document.addEventListener("keydown", this.onEscKey);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    
    // Initialize page visibility
    this.isPageVisible = !document.hidden;
  },

  beforeUnmount() {
    document.removeEventListener("fullscreenchange", this.onFullscreenChange);
    document.removeEventListener("keydown", this.onEscKey);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    
    // Clean up GameManager when component is destroyed
    if (this.gameManager) {
      this.gameManager.destroy();
      this.gameManager = null;
    }
  },
  
  async created() {
    // Fetch game data
    await this.fetchGame(this.$route.params.slug);

    // Determine if user can play
    this.canPlay = false;
    if (this.game.is_subscription == 0) {
      this.canPlay = true;
    }
    if (this.isLoggedIn && this.game.url && this.userInfo.is_subscribed) {
      this.canPlay = true;
    }
    
    // Load game using GameManager after component is ready
    if (this.canPlay) {
      // Use nextTick to ensure DOM is ready
      this.$nextTick(() => {
        setTimeout(() => {
          this.loadGameSafely();
        }, 100);
      });
    }
  },
};
</script>

<style>
.ios-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
}

.text-gray {
  color: #A4A4A4 !important;
}

.fs-7 {
  font-size: .875rem;
  line-height: 1.25rem;
}

.game-container {
  width: 100%;
  height: 70vh;
  aspect-ratio: 16/9;
}

.game-throttled {
  opacity: 0.7;
  pointer-events: none;
}

.subscription-message {
  background-color: rgba(15, 64, 134, 0.733);
  border-radius: 12px;
  margin: 0 32px;
  cursor: pointer;
}
</style>